/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDomainsRequest,
  ListDomainsSecurity,
} from "../models/operations/listdomains.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListDomainsQuery,
  ListDomainsQueryData,
  prefetchListDomains,
  queryKeyListDomains,
} from "./listDomains.core.js";
export {
  buildListDomainsQuery,
  type ListDomainsQueryData,
  prefetchListDomains,
  queryKeyListDomains,
};

export type ListDomainsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDomains domains
 *
 * @remarks
 * List the custom domains for an organization. The result is empty when no custom domain has been configured.
 */
export function useListDomains(
  request?: ListDomainsRequest | undefined,
  security?: ListDomainsSecurity | undefined,
  options?: QueryHookOptions<ListDomainsQueryData, ListDomainsQueryError>,
): UseQueryResult<ListDomainsQueryData, ListDomainsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListDomainsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDomains domains
 *
 * @remarks
 * List the custom domains for an organization. The result is empty when no custom domain has been configured.
 */
export function useListDomainsSuspense(
  request?: ListDomainsRequest | undefined,
  security?: ListDomainsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListDomainsQueryData,
    ListDomainsQueryError
  >,
): UseSuspenseQueryResult<ListDomainsQueryData, ListDomainsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListDomainsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListDomainsData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: ListDomainsQueryData,
): ListDomainsQueryData | undefined {
  const key = queryKeyListDomains(...queryKeyBase);

  return client.setQueryData<ListDomainsQueryData>(key, data);
}

export function invalidateListDomains(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "domains", "listDomains", ...queryKeyBase],
  });
}

export function invalidateAllListDomains(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "domains", "listDomains"],
  });
}
